<template>
  <section class="quote-section" aria-labelledby="quote-heading">
    <header class="head">
      <h2 id="quote-heading" class="subtitle">Cotiza con tu archivo 3D</h2>
      <p class="text">
        Sube tu modelo en formato <strong>.stl</strong>, <strong>.ply</strong>
        o <strong>.obj</strong>. Lo visualizaremos en el navegador y
        calcularemos sus dimensiones automáticamente para acelerar tu
        cotización.
      </p>
    </header>

    <div class="layout">
      <div class="left">
        <FileUploader
          :accepted-extensions="['stl', 'ply', 'obj']"
          :accepted-mime-types="[
            'model/stl',
            'application/sla',
            'application/vnd.ms-pki.stl',
            'application/octet-stream',
            'model/obj',
            'text/plain',
          ]"
          :max-size-mb="50"
          aria-label="Subir modelo 3D"
          helper-text="Arrastra tu archivo 3D aquí o haz clic para seleccionarlo"
          @file-selected="onFileSelected"
          @file-cleared="onFileCleared"
          @file-error="onFileCleared"
        />

        <ModelViewer
          v-if="file"
          :file="file"
          @model-loaded="onModelLoaded"
        />
      </div>

      <div class="right">
        <QuoteForm
          ref="quoteForm"
          :has-file="!!file"
          :file="file"
          :auto-fill-dimensions="autoFillDimensions"
          :volume-mm3="volumeMm3"
          :submitting="showPreviewModal"
          @request-preview="onRequestPreview"
        />
        <p v-if="successMessage" class="ok-msg" role="status">{{ successMessage }}</p>
      </div>
    </div>

    <QuotePreviewModal
      v-if="showPreviewModal"
      ref="previewModal"
      :payload="previewPayload"
      :file="file"
      :volume-mm3="volumeMm3"
      @confirm="onConfirmSubmit"
      @close="closePreview"
    />
  </section>
</template>

<script>
import FileUploader from './FileUploader.vue';
import ModelViewer from './ModelViewer.vue';
import QuoteForm from './QuoteForm.vue';
import QuotePreviewModal from './QuotePreviewModal.vue';
import { submitQuoteRequest } from '@/services/print3dService';

export default {
  name: 'QuoteSection',
  components: { FileUploader, ModelViewer, QuoteForm, QuotePreviewModal },
  data() {
    return {
      file: null,
      autoFillDimensions: null,
      volumeMm3: null,
      showPreviewModal: false,
      previewPayload: null,
      successMessage: '',
    };
  },
  methods: {
    onFileSelected(file) {
      this.file = file;
      this.autoFillDimensions = null;
      this.volumeMm3 = null;
      this.successMessage = '';
    },
    onFileCleared() {
      this.file = null;
      this.autoFillDimensions = null;
      this.volumeMm3 = null;
    },
    onModelLoaded(metrics) {
      this.autoFillDimensions = { ...metrics.dimensionsMm };
      // ModelViewer expone volumeCm3; el backend trabaja en mm³.
      if (metrics.volumeCm3 != null) {
        this.volumeMm3 = Number(metrics.volumeCm3) * 1000;
      }
    },
    onRequestPreview(payload) {
      this.previewPayload = payload;
      this.successMessage = '';
      this.showPreviewModal = true;
    },
    closePreview() {
      this.showPreviewModal = false;
    },
    async onConfirmSubmit() {
      const { receipt, error } = await submitQuoteRequest({
        ...this.previewPayload,
        volumeMm3: this.volumeMm3,
        file: this.file,
      });
      if (error) {
        this.$refs.previewModal?.setSubmitError(error);
        return;
      }
      this.successMessage = `Solicitud recibida (#${receipt.id}). Te contactaremos al correo ${this.previewPayload.contactEmail}.`;
      this.showPreviewModal = false;
      this.previewPayload = null;
      this.$refs.quoteForm?.reset();
    },
  },
};
</script>

<style scoped>
.quote-section {
  width: 100%;
  margin: 0 auto 4vw;
}

.head {
  margin-bottom: 1.5rem;
}

.head .subtitle {
  margin-bottom: 0.5rem;
}

.layout {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1.5rem;
  align-items: start;
}

.left {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.right {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.ok-msg {
  background: rgba(22, 163, 74, 0.1);
  color: #166534;
  padding: 0.6rem 0.8rem;
  border-radius: 6px;
  margin: 0;
  font-size: 0.9rem;
}

@media (max-width: 800px) {
  .layout {
    grid-template-columns: 1fr;
  }
}
</style>
